import dayjs from 'dayjs';

import { ExtendedOfferRequirements } from '../components/Dialogs/CreateInvite/context/types';
import {
  OfferCategory,
  OfferRequirements,
  PenaltyStatus,
  Reservation,
  ReservationStatus,
  ShippingStatus,
} from '../graphql/API';
import { getDateAndTimeInTimezoneInDayjs } from './date';

export const checkChatHoursIsNotPassed = (reservation: Reservation) => {
  const hours = reservation?.penaltyStatus === PenaltyStatus.active ? 168 : 48;
  const givenDateTimeUTC = getDateAndTimeInTimezoneInDayjs(
    reservation?.effectiveEndDateTime || '',
    reservation?.timeZoneId,
  )?.add(hours, 'hours');

  const currentDateTime = getDateAndTimeInTimezoneInDayjs(
    dayjs.utc().format(),
    reservation?.timeZoneId,
  );

  return currentDateTime?.isBefore(givenDateTimeUTC);
};

export const isAfter48HoursOfOfferEnd = ({ effectiveEndDateTime, timeZoneId }: Reservation) => {
  const givenDateTimeUTC = getDateAndTimeInTimezoneInDayjs(
    effectiveEndDateTime || '',
    timeZoneId,
  )?.add(48, 'hours');

  const currentDateTime = getDateAndTimeInTimezoneInDayjs(dayjs.utc().format(), timeZoneId);

  return currentDateTime?.isAfter(givenDateTimeUTC);
};

export const brandPromotionLimit: Record<string, number> = {
  basic: 5,
  starter: 25,
};

export const getReservationRequirementsValues = (reservation?: Reservation) => {
  if (reservation?.postOnTikTokVideo) return OfferRequirements.postOnTikTokVideo;
  if (reservation?.postOnInstagramStories) return OfferRequirements.postOnInstagramStories;
  if (reservation?.postOnInstagramStories3x) return OfferRequirements.postOnInstagramStories3x;
  if (reservation?.postOnInstagramFeed) return OfferRequirements.postOnInstagramFeed;
  if (reservation?.postOnInstagramReels) return OfferRequirements.postOnInstagramReels;
  if (
    !reservation?.postOnTikTokVideo &&
    !reservation?.postOnInstagramStories &&
    !reservation?.postOnInstagramStories3x &&
    !reservation?.postOnInstagramFeed &&
    !reservation?.postOnInstagramReels
  )
    return ExtendedOfferRequirements.noRequirement;
};

export function getReservationStatus(reservation: Reservation): ShippingStatus {
  if (reservation.shippingStatus != ShippingStatus.shipped)
    return reservation.shippingStatus as ShippingStatus;
  const now = dayjs();
  const effectiveEnd = reservation.effectiveEndDateTime
    ? dayjs(reservation.effectiveEndDateTime)
    : null;

  const isDelivered =
    (!!reservation.postLink && reservation.postLink.trim() !== '') ||
    (reservation.offer?.shopifyProductID && reservation.orderTracking?.trackingNumber) ||
    (effectiveEnd && effectiveEnd.isBefore(now.subtract(14, 'days')));

  if (isDelivered) return ShippingStatus.delivered;

  const isInTransit =
    (effectiveEnd && effectiveEnd.isAfter(now.subtract(14, 'days'))) ||
    reservation.requirementsSubmitted === false ||
    reservation.requirementsSubmitted === null ||
    (reservation.offer?.shopifyProductID && !reservation.orderTracking?.trackingNumber);

  if (isInTransit) return 'in-transit' as ShippingStatus;

  return reservation.shippingStatus as ShippingStatus;
}

export const getReservationTimeLeftToPost = (
  data: Reservation,
): 'completed' | 'notCompleted' | 'unknown' => {
  const isProductCategory = data?.offerCategory === OfferCategory.product;
  const requirementsValues = getReservationRequirementsValues(data);

  const reservationHasRequirements = requirementsValues !== ExtendedOfferRequirements.noRequirement;

  if (data?.status === ReservationStatus.checkedin) {
    const targetDate = dayjs
      .utc(isProductCategory ? data?.shippedDate : data?.checkInDateTime)
      .add(reservationHasRequirements ? 14 : 7, 'day')
      .startOf('day');
    const now = dayjs.utc().startOf('day');
    const diffInHours = targetDate.diff(now, 'hour');
    const diffInDays = targetDate.diff(now, 'day');

    if (diffInHours <= 0 && diffInDays <= 0 && !data?.requirementsSubmitted) return 'notCompleted';

    if (data?.requirementsSubmitted) return 'completed';
  }

  return 'unknown';
};

export const getShippingAddressString = (reservation?: Reservation) => {
  if (!reservation?.shippingAddress) {
    return '';
  }
  return [
    reservation.shippingAddress.address1,
    reservation?.shippingAddress.address2,
    reservation?.shippingAddress.city,
    reservation?.shippingAddress.postalCode + ' ' + reservation?.shippingAddress.stateProvince,
    reservation?.shippingAddress.country,
  ]
    .filter((i) => !!i)
    .join(', ');
};
